import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { app } from './config';

/**
 * VAPID key — the Web Push certificate public key for this Firebase project.
 *
 * How to get it:
 *   Firebase Console → Project Settings → Cloud Messaging →
 *   Web configuration → "Generate key pair" (or copy the existing key pair's
 *   public key). It is a ~88-character base64url string.
 *
 * This is a PUBLIC key — safe to commit to source. It is not a secret.
 */
export const VAPID_KEY = 'BD4wtXHoeAm_3-6lH49hWiRe45D7bBocKkmIkY2U41AFXJSoj8N0j-fVYzEO_RNlFSzKvNsojRQgeix_w7Q33sM';

let _messaging = null;

/**
 * Returns the Firebase Messaging instance, or null if FCM is not supported
 * (e.g. non-HTTPS origin, or a browser that doesn't support push).
 */
export async function getMessagingInstance() {
  if (_messaging) return _messaging;
  try {
    const supported = await isSupported();
    if (!supported) return null;
    _messaging = getMessaging(app);
    return _messaging;
  } catch {
    return null;
  }
}

/**
 * Requests notification permission (if not already granted) and returns the
 * FCM registration token for this browser/device, or null on failure.
 *
 * Firebase automatically registers the /firebase-messaging-sw.js service
 * worker when getToken() is called — no manual SW registration required.
 */
export async function requestFCMToken() {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;
  try {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    return token || null;
  } catch {
    // Thrown when permission is denied, SW is not available, or VAPID key is wrong
    return null;
  }
}

/**
 * Registers a handler for messages received while the app tab is in the
 * foreground. Returns an unsubscribe function.
 *
 * Background messages are handled by firebase-messaging-sw.js instead.
 */
export async function onForegroundMessage(callback) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}
